import { useRef, useState } from 'react'
import { registerWidget, type CellContentProps, type CellEditorProps } from '../registry'
import { controlChrome, editorBehavior } from '../editorChrome'
import { Button } from '../Button'
import type { Geometry } from 'geojson'

// Mirror of the enum served by geojson.value_schema.properties.type. The
// widget registry is static, so this literal is locked to the wire contract by
// tests/test_kind_contracts_metadata.py and the form-context contract test.
const TYPES = new Set(['Point', 'MultiPoint', 'LineString', 'MultiLineString', 'Polygon', 'MultiPolygon'])
type GeometryValue = { type: string; coordinates: unknown }

function countPoints(value: unknown): number {
  if (!Array.isArray(value)) return 0
  if (value.length >= 2 && typeof value[0] === 'number' && typeof value[1] === 'number') return 1
  return value.reduce((total, child) => total + countPoints(child), 0)
}

export function geometrySummary(value: unknown): string | null {
  if (!value || typeof value !== 'object') return null
  const geometry = value as GeometryValue
  if (!TYPES.has(geometry.type) || !Array.isArray(geometry.coordinates)) return null
  const points = countPoints(geometry.coordinates)
  if (geometry.type === 'Polygon') {
    const rings = geometry.coordinates.length
    return `Polygon · ${rings} ${rings === 1 ? 'ring' : 'rings'} · ${points} points`
  }
  if (geometry.type === 'MultiPolygon') {
    const polygons = geometry.coordinates.length
    return `MultiPolygon · ${polygons} ${polygons === 1 ? 'polygon' : 'polygons'} · ${points} points`
  }
  return `${geometry.type} · ${points} ${points === 1 ? 'point' : 'points'}`
}

/** One shared wire/object guard for the cell widget and map renderer. */
export function parseGeometryValue(value: unknown): Geometry | null {
  let candidate = value
  if (typeof value === 'string') {
    try { candidate = JSON.parse(value) } catch { return null }
  }
  return geometrySummary(candidate) ? candidate as Geometry : null
}

export function parseGeometry(text: string): { value?: Geometry; error?: string } {
  try {
    const value = parseGeometryValue(JSON.parse(text))
    if (!value) {
      return { error: 'Enter an RFC 7946 Geometry object, not a Feature or FeatureCollection.' }
    }
    return { value }
  } catch {
    return { error: 'Enter valid JSON.' }
  }
}

export function GeoJsonContent({ value }: CellContentProps) {
  if (value == null) return <span className="text-muted-foreground">—</span>
  const geometry = parseGeometryValue(value)
  const summary = geometrySummary(geometry) ?? 'GeoJSON value'
  const raw = geometry ?? value
  return (
    <details className="min-w-0">
      <summary className="cursor-pointer text-sm">{summary}</summary>
      <pre data-testid="geojson-raw" className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap font-mono text-xs text-muted-foreground">
        {typeof raw === 'string' ? raw : JSON.stringify(raw, null, 2)}
      </pre>
    </details>
  )
}

export function GeoJsonEditor({ value, onCommit, onCancel, context }: CellEditorProps) {
  const behavior = editorBehavior(context)
  const initial = value == null ? '' : typeof value === 'string' ? value : JSON.stringify(value, null, 2)
  const [draft, setDraft] = useState(initial)
  const [error, setError] = useState<string | null>(null)
  const ref = useRef<HTMLTextAreaElement>(null)
  const commit = () => {
    if (draft.trim() === '') { setError(null); onCommit(null); return }
    const parsed = parseGeometry(draft)
    if (!parsed.value) { setError(parsed.error ?? 'Invalid geometry.'); return }
    setError(null)
    onCommit(parsed.value)
  }
  return (
    <div className="space-y-2">
      <textarea ref={ref} data-testid="cell-editor" autoFocus={behavior.autoFocus} rows={8}
        value={draft} aria-invalid={Boolean(error) || undefined}
        className={`w-full resize-y rounded-sm bg-background px-2 py-1.5 font-mono text-xs outline-none ${controlChrome(context)}`}
        onChange={(event) => { setDraft(event.target.value); setError(null) }}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) { event.preventDefault(); commit() }
          if (event.key === 'Escape') { event.preventDefault(); if (behavior.escapeIsLocal) event.stopPropagation(); onCancel() }
        }} />
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
      <div className="flex gap-2">
        <Button type="button" size="xs" variant="outline" onClick={commit}>Save geometry</Button>
        {context !== 'filter' && <Button type="button" size="xs" variant="quiet"
          onClick={() => { setDraft(''); setError(null); ref.current?.focus() }}>Clear</Button>}
      </div>
    </div>
  )
}

registerWidget('geojson', { read: GeoJsonContent, write: GeoJsonEditor, defaultWidth: 240 })
